import { create } from 'zustand'
import Keycloak, { type KeycloakTokenParsed } from 'keycloak-js'
import { env } from '@/shared/lib/env'
import { useToastStore } from '@/shared/store/toastStore'
import { getDomain } from "../../../tools/domain";
import { isIdle } from "../lib/activity";


const domain = getDomain();
const keycloakConfig = {
	url: env.VITE_KEYCLOAK_URL ?? `https://auth.${domain}`,
	realm: env.VITE_KEYCLOAK_REALM,
	clientId: env.VITE_KEYCLOAK_CLIENT_ID,
};

export interface UserProfile {
    name:  string
    email: string
    sub:   string
    roles: string[]
}

export interface AuthState {
    keycloak: Keycloak | null
    isAuthenticated: boolean
    isLoading: boolean
    initError: string | null
    user: KeycloakTokenParsed | null
    userProfile: UserProfile | null
    token: string | null
    isInitialized: boolean
}

export interface AuthActions {
    initializeAuth: () => Promise<void>
    login: () => void
    logout: () => void
    getToken: () => string | null
    linkIdAustria: () => void
    configureTotp: () => void
    configureSms: () => void
    refreshToken: () => Promise<void>
}

export type AuthStore = AuthState & AuthActions

const buildAiaRedirect = (marker: string): string => {
    const url = new URL(window.location.href)
    url.searchParams.set('aia', marker)
    return url.toString()
}

const extractUserProfile = (tokenParsed: KeycloakTokenParsed | undefined): UserProfile | null => {
    if (!tokenParsed) return null
    const realmAccess = tokenParsed.realm_access as { roles?: string[] } | undefined
    return {
        name:  (tokenParsed.name  as string | undefined) ?? '',
        email: (tokenParsed.email as string | undefined) ?? '',
        sub:   tokenParsed.sub ?? '',
        roles: realmAccess?.roles?.filter(r => ['participant', 'study_team', 'administrator'].includes(r)) ?? [],
    }
}

// const keycloakConfig: KeycloakConfig = {
//     url:      env.VITE_KEYCLOAK_URL,
//     realm:    env.VITE_KEYCLOAK_REALM,
//     clientId: env.VITE_KEYCLOAK_CLIENT_ID,
// }

// Global singleton to prevent multiple instances (needs to be defined outside the store!!)
let keycloakInstance: Keycloak | null = null
let isInitializing = false
let tokenRefreshInterval: ReturnType<typeof setInterval> | null = null

export const useAuthStore = create<AuthStore>((set, get) => ({
    keycloak: null,
    isAuthenticated: false,
    isLoading: true,
    initError: null,
    user: null,
    userProfile: null,
    token: null,
    isInitialized: false,

    initializeAuth: async () => {
        const state = get()

        // Prevent multiple initializations (React.StrictMode protection)
        if (state.isInitialized || isInitializing) {
            return
        }

        isInitializing = true

        try {
            if (!keycloakInstance) {
                keycloakInstance = new Keycloak(keycloakConfig)
            }

            const authenticated = await keycloakInstance.init({
                onLoad: 'login-required',
                checkLoginIframe: false,
                pkceMethod: 'S256',
                enableLogging: import.meta.env.DEV,
                locale: 'de',
            })

            set({
                keycloak: keycloakInstance,
                isAuthenticated: authenticated,
                isLoading: false,
                isInitialized: true,
                user: authenticated ? (keycloakInstance.tokenParsed ?? null) : null,
                userProfile: authenticated ? extractUserProfile(keycloakInstance.tokenParsed) : null,
                token: authenticated ? (keycloakInstance.token ?? null) : null,
            })

            // Set up token refresh
            if (authenticated && !tokenRefreshInterval) {
                tokenRefreshInterval = setInterval(() => {
                    // Stop refreshing once the user is idle — otherwise the
                    // Keycloak SSO idle timeout is reset forever and the
                    // server-side session never expires.
                    if (isIdle()) return
                    keycloakInstance!.updateToken(70).then((refreshed) => {
                        if (refreshed) {
                            set({
                                token: keycloakInstance!.token ?? null,
                                user: keycloakInstance!.tokenParsed ?? null,
                                userProfile: extractUserProfile(keycloakInstance!.tokenParsed),
                            })
                        }
                    }).catch(() => {
                        console.error('Failed to refresh token')
                        useToastStore.getState().show('Your session has expired. Please log in again.', 'warning')
                        get().logout()
                    })
                }, 60000)
            }
        } catch (error) {
            console.error('Failed to initialize Keycloak:', error)
            const message = error instanceof Error ? error.message : 'Authentication service unavailable'
            set({ isLoading: false, isAuthenticated: false, isInitialized: true, initError: message })
        } finally {
            isInitializing = false
        }
    },

    login: () => {
        const { keycloak } = get()
        if (keycloak) keycloak.login({ locale: 'de' })
    },

    logout: () => {
        const { keycloak } = get()
        if (keycloak) {
            if (tokenRefreshInterval !== null) {
                clearInterval(tokenRefreshInterval)
                tokenRefreshInterval = null
            }
            set({ isAuthenticated: false, user: null, userProfile: null, token: null })
            // Always return to the app root so the next login (possibly a
            // different user/role) never lands on a role-restricted deep link.
            keycloak.logout({ redirectUri: window.location.origin })
        }
    },

    getToken: () => get().token,

    // Application-initiated action: round-trips through Keycloak -> ID Austria
    // -> back to the current page with a kc_action_status query parameter.
    linkIdAustria: () => {
        const { keycloak } = get()
        if (keycloak) {
            keycloak.login({
                action: 'idp_link:id-austria',
                redirectUri: window.location.href,
                locale: 'de',
            })
        }
    },

    // Self-service 2FA enrollment via application-initiated actions. Each
    // appends its own ?aia marker to the return URL so the profile page can
    // tell which action completed (kc_action_status alone does not identify it).
    configureTotp: () => {
        const { keycloak } = get()
        if (keycloak) {
            keycloak.login({ action: 'CONFIGURE_TOTP', redirectUri: buildAiaRedirect('totp'), locale: 'de' })
        }
    },

    configureSms: () => {
        const { keycloak } = get()
        if (keycloak) {
            keycloak.login({ action: 'mobile_number_config', redirectUri: buildAiaRedirect('sms'), locale: 'de' })
        }
    },

    refreshToken: async () => {
        const { keycloak } = get()
        if (!keycloak) return
        await keycloak.updateToken(-1)
        set({
            token: keycloak.token ?? null,
            user: keycloak.tokenParsed ?? null,
            userProfile: extractUserProfile(keycloak.tokenParsed),
        })
    },
}))
